import React, { useEffect } from "react";
import { ShieldCheck, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface ToastMsgProps {
  onClose: () => void;
  message: string;
  show: boolean;
  bg?: string;
  index?: number | string;
  Toastheader?: string;
}

const ToastMsg: React.FC<ToastMsgProps> = ({
  onClose,
  message,
  show,
  bg = "success",
  index,
  Toastheader
}) => {
  // Auto-hide after 5 seconds
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  // Map status types to styling configuration exactly matching the reference design
  const getTypeStyles = () => {
    const normalized = (bg || "success").toLowerCase();

    switch (normalized) {
      case "danger":
      case "error":
        return {
          icon: (
            <div className="cht-w-11 cht-h-11 cht-rounded-full cht-bg-[#eb5757] cht-shadow-[0_8px_18px_rgba(235,87,87,0.38)] cht-flex cht-items-center cht-justify-center cht-shrink-0">
              <X size={20} strokeWidth={3} className="cht-text-white" />
            </div>
          ),
          cardBg: "#fff0f0",
          borderColor: "#fcd0d0",
          defaultHeader: "Something went wrong!",
        };
      case "warning":
      case "warn":
        return {
          icon: (
            <div className="cht-w-11 cht-h-11 cht-rounded-full cht-bg-[#f59e0b] cht-shadow-[0_8px_18px_rgba(245,158,11,0.38)] cht-flex cht-items-center cht-justify-center cht-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="cht-text-white">
                <rect x="10.5" y="4.5" width="3" height="9.5" rx="1.5" />
                <circle cx="12" cy="18" r="1.85" />
              </svg>
            </div>
          ),
          cardBg: "#fff8eb",
          borderColor: "#fae3b6",
          defaultHeader: "Warning",
        };
      case "info":
      case "primary":
        return {
          icon: (
            <div className="cht-w-11 cht-h-11 cht-rounded-full cht-bg-[#2f80ed] cht-shadow-[0_8px_18px_rgba(47,128,237,0.38)] cht-flex cht-items-center cht-justify-center cht-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="cht-text-white">
                <circle cx="12" cy="6.5" r="2.2" />
                <rect x="10.5" y="10.5" width="3" height="8.5" rx="1.5" />
              </svg>
            </div>
          ),
          cardBg: "#edf5ff",
          borderColor: "#c7ddfa",
          defaultHeader: "Did you know?",
        };
      case "success":
      default:
        return {
          icon: (
            <div className="cht-w-11 cht-h-11 cht-rounded-full cht-bg-[#27d071] cht-shadow-[0_8px_18px_rgba(39,208,113,0.38)] cht-flex cht-items-center cht-justify-center cht-shrink-0">
              <ShieldCheck size={21} strokeWidth={2.4} className="cht-text-white" />
            </div>
          ),
          cardBg: "#eefaf2",
          borderColor: "#c2efcf",
          defaultHeader: "Congratulations!",
        };
    }
  };

  const config = getTypeStyles();
  const headerText = Toastheader || config.defaultHeader;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          layout
          initial={{ opacity: 0, y: -20, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -12, scale: 0.92, transition: { duration: 0.18 } }}
          transition={{ type: "spring", stiffness: 380, damping: 26 }}
          className="cht-flex cht-items-center cht-justify-between cht-gap-3.5 sm:cht-gap-4 cht-w-[calc(100vw-2.5rem)] sm:cht-w-auto sm:cht-min-w-[360px] sm:cht-max-w-[460px] cht-p-3.5 sm:cht-py-4 sm:cht-px-5 cht-rounded-[22px] cht-border-[1.5px] cht-shadow-[0_12px_32px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]"
          style={{
            backgroundColor: config.cardBg,
            borderColor: config.borderColor
          }}
          key={index}
        >
          {/* Leading Status Icon Badge with Ambient Shadow */}
          <div className="cht-shrink-0">
            {config.icon}
          </div>

          {/* Text Content */}
          <div className="cht-flex-1 cht-min-w-0 cht-pr-1">
            <h6 className="cht-text-[13px] sm:cht-text-[13px] cht-font-bold cht-text-slate-900 pb-0 mb-0 !cht-font-body cht-tracking-tight cht-leading-snug">
              {headerText}
            </h6>
            <p className="cht-text-xs sm:cht-text-[13px] cht-text-slate-600 !cht-font-body pb-0 mb-0 cht-leading-relaxed cht-break-words">
              {message}
            </p>
          </div>

          {/* Dismiss Button matching reference screenshot */}
          <button
            type="button"
            onClick={onClose}
            className="cht-w-8 cht-h-8 cht-rounded-xl cht-bg-white/80 hover:cht-bg-white cht-border cht-border-black/5 hover:cht-border-black/10 cht-shadow-xs cht-flex cht-items-center cht-justify-center cht-text-slate-600 hover:cht-text-slate-900 hover:cht-scale-105 cht-transition-all cht-cursor-pointer cht-shrink-0"
            title="Dismiss"
          >
            <X size={15} strokeWidth={2.4} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ToastMsg;
